import React, { useCallback, useState } from 'react';
import { Heart, Save, RefreshCw, Download, User, Activity, GitBranch } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import BasicParametersSection from './BasicParametersSection';
import AdvancedParametersSection from './AdvancedParametersSection';
import FormulaSelectionSection from './FormulaSelectionSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger, isInRange } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета максимального пульса и зон пульса
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  mhrFormulas,
  activityLevels,
  fitnessLevels,
  sportTypes,
  saveProfile 
}) => {
  
  // Состояние для имени сохраняемого профиля
  const [saveProfileName, setSaveProfileName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeRestingHeartRate' && !checked) {
        newState.restingHeartRate = '60';
      }
      
      if (name === 'includeFitnessAdjustment' && !checked) {
        newState.fitnessLevel = 'average';
      }

      // Переключаем на формулу Гулати для женщин
      if (name === 'gender' && value === 'female') {
        // Формула Гулати обычно рекомендуется для женщин
        if (prev.formula === 'tanaka' || prev.formula === 'haskell') {
          newState.formula = 'gulati';
        }
      } else if (name === 'gender' && value === 'male' && prev.formula === 'gulati') {
        // Для мужчин лучше использовать формулу Танаки
        newState.formula = 'tanaka';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      age, gender, restingHeartRate, activityLevel,
      formula, includeRestingHeartRate, includeFitnessAdjustment, fitnessLevel, sportSpecific
    } = formData;

    // Парсинг числовых значений
    const numAge = parseIntOrDefault(age, -1);
    const numRestingHeartRate = parseIntOrDefault(restingHeartRate, -1);

    // Валидация базовых полей
    if (!isInteger(age) || !isInRange(numAge, 1, 120)) {
      newErrors.age = 'Возраст должен быть от 1 до 120 лет.';
    }
    
    if (!gender || !['male', 'female'].includes(gender)) {
      newErrors.gender = 'Выберите пол.';
    }

    // Валидация пульса в покое
    if (includeRestingHeartRate) {
      if (!isInteger(restingHeartRate) || !isInRange(numRestingHeartRate, 30, 100)) {
        newErrors.restingHeartRate = 'Пульс в покое должен быть от 30 до 100 ударов в минуту.';
      }
    }

    // Валидация выбора формулы
    if (!formula || !mhrFormulas.some(f => f.id === formula)) {
      newErrors.formula = 'Выберите формулу расчета.';
    }

    // Валидация уровня активности
    if (!activityLevel || !activityLevels.some(l => l.id === activityLevel)) {
      newErrors.activityLevel = 'Выберите уровень активности.';
    }

    // Валидация уровня подготовки
    if (includeFitnessAdjustment && (!fitnessLevel || !fitnessLevels.some(l => l.id === fitnessLevel))) {
      newErrors.fitnessLevel = 'Выберите уровень подготовки.';
    }

    // Валидация типа спорта
    if (!sportSpecific || !sportTypes.some(s => s.id === sportSpecific)) {
      newErrors.sportSpecific = 'Выберите тип физической активности.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, mhrFormulas, activityLevels, fitnessLevels, sportTypes, setErrors]);

  // Функция расчета максимального пульса
  const calculateMaxHeartRate = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const numAge = parseIntOrDefault(formData.age);
        const gender = formData.gender;
        const numRestingHeartRate = parseIntOrDefault(formData.restingHeartRate);
        const formulaId = formData.formula;
        const includeRestingHeartRate = formData.includeRestingHeartRate;
        const includeFitnessAdjustment = formData.includeFitnessAdjustment;
        const fitnessLevelId = formData.fitnessLevel;
        const sportSpecificId = formData.sportSpecific;
        const activityLevelId = formData.activityLevel;

        // Получаем коэффициенты из выбранных опций
        const activityLevel = activityLevels.find(level => level.id === activityLevelId);
        const fitnessLevel = fitnessLevels.find(level => level.id === fitnessLevelId);
        const sportType = sportTypes.find(sport => sport.id === sportSpecificId);
        
        // Коэффициент активности (для корректировки MHR)
        const activityCoef = activityLevel?.coef || 1.0;
        
        // Коэффициент уровня подготовки (если выбран)
        const fitnessCoef = includeFitnessAdjustment ? (fitnessLevel?.coef || 1.0) : 1.0;
        
        // --- Расчет максимального пульса ---
        let maxHeartRate = 0;

        // Применяем выбранную формулу
        switch (formulaId) {
          case 'tanaka':
            maxHeartRate = 208 - (0.7 * numAge);
            break;
          case 'haskell':
            maxHeartRate = 220 - numAge;
            break;
          case 'gellish':
            maxHeartRate = 207 - (0.7 * numAge);
            break;
          case 'nes':
            maxHeartRate = 211 - (0.64 * numAge);
            break;
          case 'gulati':
            // Формула Гулати специфична для женщин
            maxHeartRate = 206 - (0.88 * numAge);
            break;
          default:
            // По умолчанию используем формулу Танаки как наиболее точную
            maxHeartRate = 208 - (0.7 * numAge);
        }

        // Применяем коэффициент активности
        maxHeartRate *= activityCoef;
        
        // Применяем коэффициент уровня подготовки (если включено)
        if (includeFitnessAdjustment) {
          maxHeartRate *= fitnessCoef;
        }
        
        // Округляем до целого числа
        maxHeartRate = Math.round(maxHeartRate);
        
        // Корректировка MHR в зависимости от типа активности (например, в плавании пульс ниже)
        if (sportType && sportType.zoneAdjustment.hr !== 0) {
          maxHeartRate += sportType.zoneAdjustment.hr;
        }

        // --- Расчет зон пульса ---
        const zones = calculateHeartRateZones(
          maxHeartRate, 
          includeRestingHeartRate ? numRestingHeartRate : null,
          sportType
        );

        // --- Расчет рекомендаций и дополнительных данных ---
        const activityTypeName = sportType?.name || 'общей физической активности';
        
        // Определение оптимальной зоны для разных целей
        const fatBurningZone = {
            min: zones[1].min,
            max: zones[2].min
        };
        
        const cardiovascularZone = {
            min: zones[2].min,
            max: zones[3].min
        };
        
        const anaerobicZone = {
            min: zones[3].min,
            max: zones[4].min
        };
        
        // Формируем рекомендации
        const recommendations = {
            warmUp: `Разминка: ${zones[0].min}-${zones[0].max} уд/мин`,
            fatBurning: `Сжигание жира: ${fatBurningZone.min}-${fatBurningZone.max} уд/мин`,
            cardio: `Кардио тренировки: ${cardiovascularZone.min}-${cardiovascularZone.max} уд/мин`,
            anaerobic: `Анаэробная тренировка: ${anaerobicZone.min}-${anaerobicZone.max} уд/мин`,
            general: `Для ${activityTypeName} рекомендуется тренироваться в диапазоне от ${zones[1].min} до ${zones[3].max} уд/мин в зависимости от целей тренировки.`,
            recovery: `Пульс восстановления не должен превышать ${zones[1].min} уд/мин через 5 минут после тренировки.`
        };

        // Подготовка финальных результатов
        const finalResults = {
            maxHeartRate,
            age: numAge,
            gender,
            restingHeartRate: includeRestingHeartRate ? numRestingHeartRate : null,
            activityLevel: activityLevelId,
            activityLevelName: activityLevel?.name || '',
            formula: formulaId,
            formulaName: mhrFormulas.find(f => f.id === formulaId)?.name || '',
            zones,
            fatBurningZone,
            cardiovascularZone,
            anaerobicZone,
            recommendations,
            fitnessLevel: includeFitnessAdjustment ? fitnessLevelId : null,
            fitnessLevelName: includeFitnessAdjustment ? fitnessLevel?.name || '' : null,
            sportSpecific: sportSpecificId,
            sportSpecificName: sportType?.name || ''
        };

        setResults(finalResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, activityLevels, fitnessLevels, sportTypes, mhrFormulas]);

  // Функция расчета зон пульса
  const calculateHeartRateZones = (maxHeartRate, restingHeartRate = null, sportType = null) => {
    // Зоны пульса (в процентах от максимального или резерва пульса)
    const zonePercentages = [
      { min: 50, max: 60, name: 'Восстановительная', description: 'Разминка и расслабление' },
      { min: 60, max: 70, name: 'Легкая аэробная', description: 'Сжигание жира и базовая выносливость' },
      { min: 70, max: 80, name: 'Аэробная', description: 'Улучшение выносливости и кардио' },
      { min: 80, max: 90, name: 'Анаэробная', description: 'Повышение результативности' },
      { min: 90, max: 100, name: 'Максимальная', description: 'Максимальная нагрузка' }
    ];

    // Функция для расчета пульса от процента макс. ЧСС
    const calculateHeartRate = (percent) => {
      // Метод Карвонена (с учетом пульса в покое)
      if (restingHeartRate !== null) {
        return Math.round(restingHeartRate + (maxHeartRate - restingHeartRate) * (percent / 100));
      }
      // Стандартный метод (процент от максимального пульса)
      return Math.round(maxHeartRate * (percent / 100));
    };

    // Рассчитываем зоны пульса с учетом корректировок для конкретного вида спорта
    return zonePercentages.map((zone, index) => {
      // Применяем корректировки для конкретного вида спорта
      let zoneMin = zone.min;
      let zoneMax = zone.max;

      if (sportType) {
        // Корректировка в зависимости от типа зоны
        const adjustments = {
          0: sportType.zoneAdjustment.recovery || 0, // Восстановительная
          1: sportType.zoneAdjustment.aerobic || 0,  // Легкая аэробная
          2: sportType.zoneAdjustment.aerobic || 0,  // Аэробная
          3: sportType.zoneAdjustment.anaerobic || 0, // Анаэробная
          4: sportType.zoneAdjustment.maximal || 0   // Максимальная
        };

        if (adjustments[index] !== 0) {
          zoneMin += adjustments[index];
          zoneMax += adjustments[index];
        }
      }

      return {
        name: zone.name,
        description: zone.description,
        min: calculateHeartRate(zoneMin),
        max: calculateHeartRate(zoneMax),
        minPercent: zoneMin,
        maxPercent: zoneMax
      };
    });
  };

  // Сброс формы
  const resetForm = () => {
    setFormData({
      age: '30',
      gender: 'male',
      restingHeartRate: '60',
      activityLevel: 'moderate',
      formula: 'tanaka',
      includeRestingHeartRate: true,
      includeFitnessAdjustment: false,
      fitnessLevel: 'average',
      sportSpecific: 'general',
    });
    setResults(null);
    setErrors({});
    setSaveProfileName('');
    setShowSaveDialog(false);
  };

  // Экспорт зон пульса в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки для CSV
    const headers = ['Зона', 'Название', 'Описание', 'Мин. пульс (уд/мин)', 'Макс. пульс (уд/мин)', 'Мин. %', 'Макс. %'];

    // Подготавливаем данные о зонах пульса
    const rows = results.zones.map((zone, index) => {
      return [
        index + 1,
        zone.name,
        zone.description,
        zone.min,
        zone.max,
        zone.minPercent + '%',
        zone.maxPercent + '%'
      ].join(';');
    });

    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Максимальный пульс:', results.maxHeartRate],
      ['Возраст:', results.age],
      ['Пол:', results.gender === 'male' ? 'Мужской' : 'Женский'],
      ['Формула расчета:', results.formulaName],
      ['Уровень активности:', results.activityLevelName],
      ['Пульс в покое:', results.restingHeartRate || 'Не указан'],
      ['Вид физической активности:', results.sportSpecificName],
      ['Дата расчета:', new Date().toLocaleDateString('ru-RU')],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'зоны_пульса.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения профиля
  const handleSaveProfile = () => {
    if (showSaveDialog) {
      const savedResult = saveProfile(saveProfileName || null);
      if (savedResult) {
        alert(`Профиль "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveProfileName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateMaxHeartRate();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Heart className="w-6 h-6 text-red-500" />
          Расчет максимального пульса
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры */}
        <BasicParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          activityLevels={activityLevels}
        />

        {/* Выбор формулы расчета */}
        <FormulaSelectionSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          mhrFormulas={mhrFormulas}
        />

        {/* Дополнительные параметры */}
        <AdvancedParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          fitnessLevels={fitnessLevels}
          sportTypes={sportTypes}
        />

        {/* Диалог сохранения профиля */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveProfileName}
                onChange={(e) => setSaveProfileName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveProfile}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Activity className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать пульс'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать зоны (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveProfile}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить профиль
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;